import React, { useState, useEffect, useRef } from 'react';

/**
 * WEBHOOK EVENT VIEWER
 *
 * Real-time display of webhook events from Resend
 * Shows ACTUAL events as they happen
 */

interface WebhookEvent {
  id: string;
  type: string;
  messageId: string;
  timestamp: Date;
  status: 'success' | 'failed';
  data: any;
  raw?: string;
}

export default function WebhookEventViewer() {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const eventsEndRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (autoScroll && eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events, autoScroll]);

  useEffect(() => {
    // Load any existing events from localStorage
    const stored = localStorage.getItem('webhook-events');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setEvents(parsed.map((e: any) => ({
          ...e,
          timestamp: new Date(e.timestamp)
        })));
      } catch (e) {
        console.error('Failed to parse stored events');
      }
    }
  }, []);

  const startPolling = async () => {
    setIsPolling(true);

    // Poll the worker for new events
    const pollForEvents = async () => {
      try {
        const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/webhook-events');
        if (response.ok) {
          const data = await response.json();
          if (data.events && data.events.length > 0) {
            const newEvents = data.events.map((e: any) => ({
              id: e.id || `evt_${Date.now()}_${Math.random()}`,
              type: e.type,
              messageId: e.data?.email_id || e.message_id || 'unknown',
              timestamp: new Date(e.created_at || e.timestamp),
              status: 'success' as const,
              data: e.data || e,
              raw: JSON.stringify(e, null, 2)
            }));

            setEvents(prev => {
              const updated = [...prev, ...newEvents];
              // Store in localStorage
              localStorage.setItem('webhook-events', JSON.stringify(updated));
              return updated;
            });
          }
        }
      } catch (error) {
        console.error('Failed to poll for events:', error);
      }
    };

    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(pollForEvents, 2000);
    pollForEvents(); // Initial poll
  };

  const stopPolling = () => {
    setIsPolling(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const clearEvents = () => {
    setEvents([]);
    localStorage.removeItem('webhook-events');
    setSelectedEvent(null);
  };

  const sendTestEmail = async () => {
    // Trigger a test email to generate webhook events
    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: 'webhook-test@example.com',
          code: Math.floor(100000 + Math.random() * 900000).toString(),
          testMode: true
        })
      });

      const data = await response.json();

      // Add a local event to track the send
      const sendEvent: WebhookEvent = {
        id: `local_${Date.now()}`,
        type: 'email.sent',
        messageId: data.messageId || 'pending',
        timestamp: new Date(),
        status: response.ok ? 'success' : 'failed',
        data: data
      };

      setEvents(prev => [...prev, sendEvent]);
    } catch (error) {
      console.error('Failed to send test email:', error);
    }
  };

  // Add sample events from Resend (based on your screenshot)
  const addResendEvents = () => {
    const resendEvents: WebhookEvent[] = [
      {
        id: 'evt_resend_1',
        type: 'email.sent',
        messageId: 'msg_339xWgcJj245vOBCtlUWmUKoZQt',
        timestamp: new Date(Date.now() - 60000),
        status: 'success',
        data: {
          from: 'onboarding@resend.dev',
          to: ['hi@futr.bet'],
          subject: 'Your FutrBet code: 258031'
        }
      },
      {
        id: 'evt_resend_2',
        type: 'email.delivered',
        messageId: 'msg_339xXGOpZ8iyAeMFW9VSWgLNlzb',
        timestamp: new Date(Date.now() - 30000),
        status: 'success',
        data: {
          delivered_at: new Date(Date.now() - 30000).toISOString(),
          smtp_response: '250 2.0.0 OK'
        }
      }
    ];

    setEvents(prev => [...prev, ...resendEvents]);
  };

  const getEventColor = (type: string) => {
    if (type.includes('sent')) return 'text-yellow-400 border-yellow-400';
    if (type.includes('delivered')) return 'text-green-400 border-green-400';
    if (type.includes('opened')) return 'text-blue-400 border-blue-400';
    if (type.includes('clicked')) return 'text-purple-400 border-purple-400';
    if (type.includes('bounced')) return 'text-red-400 border-red-400';
    if (type.includes('complained')) return 'text-orange-400 border-orange-400';
    return 'text-gray-400 border-gray-400';
  };

  const getEventIcon = (type: string) => {
    if (type.includes('sent')) return '📤';
    if (type.includes('delivered')) return '✅';
    if (type.includes('opened')) return '📧';
    if (type.includes('clicked')) return '🔗';
    if (type.includes('bounced')) return '❌';
    if (type.includes('complained')) return '⚠️';
    return '📨';
  };

  const filteredEvents = filter === 'all'
    ? events
    : events.filter(e => e.type.includes(filter));

  const eventStats = {
    total: events.length,
    sent: events.filter(e => e.type.includes('sent')).length,
    delivered: events.filter(e => e.type.includes('delivered')).length,
    opened: events.filter(e => e.type.includes('opened')).length,
    bounced: events.filter(e => e.type.includes('bounced')).length
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🪝 WEBHOOK EVENT VIEWER</h1>
          <p className="text-gray-400">Real-time webhook events from Resend</p>
          <p className="text-yellow-400 text-sm mt-2">
            {isPolling ? '🔴 LIVE - Polling for events...' : '⚪ Not polling'}
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="border border-gray-600 p-3 text-center">
            <div className="text-2xl font-bold">{eventStats.total}</div>
            <div className="text-xs text-gray-400">Total Events</div>
          </div>
          <div className="border border-yellow-400 p-3 text-center">
            <div className="text-2xl font-bold text-yellow-400">{eventStats.sent}</div>
            <div className="text-xs text-gray-400">Sent</div>
          </div>
          <div className="border border-green-400 p-3 text-center">
            <div className="text-2xl font-bold text-green-400">{eventStats.delivered}</div>
            <div className="text-xs text-gray-400">Delivered</div>
          </div>
          <div className="border border-blue-400 p-3 text-center">
            <div className="text-2xl font-bold text-blue-400">{eventStats.opened}</div>
            <div className="text-xs text-gray-400">Opened</div>
          </div>
          <div className="border border-red-400 p-3 text-center">
            <div className="text-2xl font-bold text-red-400">{eventStats.bounced}</div>
            <div className="text-xs text-gray-400">Bounced</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Left: Controls */}
          <div className="space-y-4">
            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={isPolling ? stopPolling : startPolling}
                  className={`w-full p-3 font-bold ${
                    isPolling
                      ? 'bg-red-400 text-black hover:bg-red-300'
                      : 'bg-green-400 text-black hover:bg-green-300'
                  }`}
                >
                  {isPolling ? '⏹️ STOP POLLING' : '▶️ START POLLING'}
                </button>

                <button
                  onClick={sendTestEmail}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300"
                >
                  📧 SEND TEST EMAIL
                </button>

                <button
                  onClick={addResendEvents}
                  className="w-full bg-purple-400 text-black p-3 hover:bg-purple-300"
                >
                  📥 ADD SAMPLE EVENTS
                </button>

                <button
                  onClick={clearEvents}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🗑️ CLEAR ALL
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="border border-yellow-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">🔍 FILTERS</h2>

              <div className="space-y-2">
                {['all', 'sent', 'delivered', 'opened', 'clicked', 'bounced'].map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`w-full p-2 text-left ${
                      filter === f
                        ? 'bg-green-900 border border-green-400'
                        : 'border border-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {f === 'all' ? '📊 All Events' : `${getEventIcon(f)} ${f}`}
                    <span className="float-right text-xs text-gray-500">
                      {f === 'all'
                        ? events.length
                        : events.filter(e => e.type.includes(f)).length}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="border border-cyan-400 p-4">
              <h2 className="text-xl mb-4 text-cyan-400">⚙️ OPTIONS</h2>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                />
                <span>Auto-scroll to new events</span>
              </label>
            </div>
          </div>

          {/* Middle: Event List */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📋 EVENTS</h2>

            <div className="h-[600px] overflow-y-auto space-y-2">
              {filteredEvents.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  {isPolling ? 'Waiting for webhook events...' : 'Click "Start Polling" to begin'}
                </div>
              ) : (
                filteredEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={`border p-2 cursor-pointer hover:bg-gray-900 ${
                      selectedEvent?.id === event.id
                        ? 'border-green-400 bg-gray-900'
                        : 'border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-bold ${getEventColor(event.type).split(' ')[0]}`}>
                        {getEventIcon(event.type)} {event.type}
                      </span>
                      <span className={`text-xs ${
                        event.status === 'success' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {event.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      <div>{event.timestamp.toLocaleTimeString()}</div>
                      <div className="truncate">ID: {event.messageId}</div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>

          {/* Right: Event Details */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">🔍 EVENT DETAILS</h2>

            {selectedEvent ? (
              <div className="space-y-4">
                <div className="bg-gray-900 p-3 rounded">
                  <h3 className={`font-bold mb-2 ${getEventColor(selectedEvent.type).split(' ')[0]}`}>
                    {getEventIcon(selectedEvent.type)} {selectedEvent.type}
                  </h3>
                  <div className="text-xs space-y-1">
                    <div><span className="text-gray-500">ID:</span> {selectedEvent.id}</div>
                    <div><span className="text-gray-500">Message:</span> {selectedEvent.messageId}</div>
                    <div><span className="text-gray-500">Time:</span> {selectedEvent.timestamp.toLocaleString()}</div>
                    <div><span className="text-gray-500">Status:</span> {selectedEvent.status}</div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm text-yellow-400 mb-2">Event Data</h3>
                  <pre className="text-xs bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(selectedEvent.data, null, 2)}
                  </pre>
                </div>

                {selectedEvent.raw && (
                  <div>
                    <h3 className="text-sm text-cyan-400 mb-2">Raw Payload</h3>
                    <pre className="text-xs bg-gray-900 p-2 rounded overflow-x-auto max-h-64 overflow-y-auto">
{selectedEvent.raw}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-600 text-center py-8">
                Select an event to see details
              </div>
            )}
          </div>
        </div>

        {/* Bottom: Connection Info */}
        <div className="mt-6 border border-cyan-400 p-4">
          <h2 className="text-xl text-cyan-400 mb-2">📡 WEBHOOK CONNECTION</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-400">Endpoint:</span>
              <div className="text-xs text-green-400 mt-1">
                https://futr-bet-api.b-9f2.workers.dev/api/webhooks/email
              </div>
            </div>
            <div>
              <span className="text-gray-400">Status:</span>
              <div className="text-xs mt-1">
                {isPolling ? '🟢 Polling Active' : '⚪ Inactive'}
              </div>
            </div>
            <div>
              <span className="text-gray-400">Events Received:</span>
              <div className="text-xs text-yellow-400 mt-1">
                {events.length} total
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}